  export const BASE_URL = "https://api.verisaya.com";
// export const BASE_URL = "http://localhost:5000";
const API_URL = `${BASE_URL}/api`;

const api = async (endpoint, options = {}) => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${API_URL}${endpoint}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  });

  const data = await res.json();

  // auto-logout on 401 Unauthorized
  if (res.status === 401) {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.__queryClient?.clear();
    window.location.href = "/login";
    return;
  }

  if (!res.ok) throw new Error(data.message || "Something went wrong");
  return data;
};

// ─── Auth ───────────────────────────────────────────
export const loginUser = (email, password) =>
  api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });

export const checkEmail = (email) =>
  api("/auth/check-email", { method: "POST", body: JSON.stringify({ email }) });

export const forgotPassword = (email) =>
  api("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) });

export const getMe = () => api("/auth/me");
export const getProfile = () => api("/auth/me");
export const updateProfile = (data) => api("/profile", { method: "PATCH", body: JSON.stringify(data) });
export const uploadProfilePhoto = (file) => {
  const formData = new FormData();
  formData.append("photo", file);
  return fetch(`${API_URL}/profile`, {
    method: "PATCH",
    credentials: "include",
    body: formData,
  }).then((res) => res.json());
};

// ─── Employees ──────────────────────────────────────
export const getEmployees = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return api(`/employees${query ? `?${query}` : ""}`);
};

export const getDepartments = () => api("/employees/departments");
export const searchEmployees = (params) => api(`/employees/search?${new URLSearchParams(params).toString()}`);

export const getEmployee = (id) => api(`/employees/${id}`);

export const createEmployee = (data) =>
  api("/employees", { method: "POST", body: JSON.stringify(data) });

export const updateEmployee = (id, data) =>
  api(`/employees/${id}`, { method: "PUT", body: JSON.stringify(data) });

export const deleteEmployee = (id) =>
  api(`/employees/${id}`, { method: "DELETE" });

export const bulkImportEmployees = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return fetch(`${API_URL}/employees/bulk-import`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(async (res) => {
    const data = await res.json();
    if (res.status !== 200 && res.status !== 201) throw new Error(data.message || "Bulk import failed");
    return data;
  });
};

export const logoutUser = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  return api("/auth/logout", { method: "POST" });
};

export const getAttendance = () => api("/attendance");
export const getEmployeeSummary = () => api("/attendance/employee-summary");
export const getDepartmentSummary = (query = "") => api(`/dashboard/department-summary${query}`);
export const getDepartmentDetail = (name) => api(`/dashboard/department/${encodeURIComponent(name)}`);
export const getTrainerSummary = () => api("/sessions/trainer-summary");
export const getDashboard = () => api("/dashboard");
export const getTopPerformers = () => api("/dashboard/top-performers");
export const getAttendanceWithSessions = async () => {
  const [attendanceData, sessionsData] = await Promise.all([
    api("/attendance"),
    api("/sessions"),
  ]);
  const records = attendanceData?.records ?? (Array.isArray(attendanceData) ? attendanceData : []);
  const sessions = sessionsData?.sessions ?? (Array.isArray(sessionsData) ? sessionsData : []);

  // build a map: sessionTitle -> { trainer, category }
  const sessionMap = {};
  sessions.forEach((s) => {
    const info = {
      trainer:  s.trainer || s.trainerName || s.createdByName || "",
      category: s.type || s.category || "",
      location: s.location || "",
      date:     s.date || "",
    };
    if (s.title) sessionMap[s.title] = info;
    if (s._id)   sessionMap[s._id]   = info;
  });

  const enriched = records.map((r) => {
    const match = sessionMap[r.session] || sessionMap[r.sessionId] || sessionMap[r.sessionName] || {};
    return {
      ...r,
      trainer:     r.trainer  || match.trainer  || r.createdBy || "",
      category:    r.category || match.category || "",
      location:    r.location || match.location || "",
      sessionDate: match.date || r.date || r.clockIn || "",
    };
  });

  return { ...attendanceData, records: enriched, sessions };
};

// ─── Sessions ─────────────────────────────────────
export const createSession = (data) =>
  api("/sessions", { method: "POST", body: JSON.stringify(data) });

export const getSessions = () => api("/sessions");

export const getSessionById = (id) => api(`/sessions/${id}`);

export const updateSession = (id, data) =>
  api(`/sessions/${id}`, { method: "PUT", body: JSON.stringify(data) });

export const deleteSession = (id) =>
  api(`/sessions/${id}`, { method: "DELETE" });

// ─── Organizations ────────────────────────────────
export const getOrganizations = () => api("/organizations");
export const getOrganizationHistory = (id) => api(`/organizations/${id}/history`);

export const createOrganization = (data) =>
  api("/organizations", { method: "POST", body: JSON.stringify(data) });
export const updateOrganization = (id, data) =>
  api(`/organizations/${id}`, { method: "PATCH", body: JSON.stringify(data) });

// ─── Renewals ──────────────────────────────────────
export const sendRenewalReminder = (organizationId) =>
  api("/renewals/send-reminder", { method: "POST", body: JSON.stringify({ organizationId }) });

// ─── Subscriptions ─────────────────────────────────
export const getSubscriptions = () => api("/subscriptions");
export const createSubscription = (data) => api("/subscriptions", { method: "POST", body: JSON.stringify(data) });
export const updateSubscription = (id, data) => api(`/subscriptions/${id}`, { method: "PUT", body: JSON.stringify(data) });
export const deleteSubscription = (id) => api(`/subscriptions/${id}`, { method: "DELETE" });

// ─── Roles ──────────────────────────────────────────
export const getRoles = () => api("/roles");

export const createRole = (data) =>
  api("/roles", { method: "POST", body: JSON.stringify(data) });

export const updateRole = (id, data) =>
  api(`/roles/${id}`, { method: "PUT", body: JSON.stringify(data) });

export const deleteRole = (id) =>
  api(`/roles/${id}`, { method: "DELETE" });

export const submitContactForm = (data) =>
  api("/contact", { method: "POST", body: JSON.stringify(data) });

// ─── Support Tickets ────────────────────────────────
// formData fields: subject (text), description (text), images (file, multiple, max 5)
export const createTicket = (organizationId, formData) => {
  const token = localStorage.getItem("token");
  return fetch(`${API_URL}/organizations/${organizationId}/tickets`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: formData,
  }).then(async (res) => {
    const data = await res.json();
    if (res.status === 401) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      window.__queryClient?.clear();
      window.location.href = "/login";
      return;
    }
    if (!res.ok) throw new Error(data.message || "Failed to raise ticket");
    return data;
  });
};

export const getTickets = (organizationId) => api(`/organizations/${organizationId}/tickets`);
export const getAllTickets = () => api("/organizations/all-tickets");
export const updateTicketStatus = (organizationId, ticketId, status) =>
  api(`/organizations/${organizationId}/tickets/${ticketId}/status`, {
    method: "PATCH",
    body: JSON.stringify({
      status,
    }),
  });


export default api;
